import { SafetyCertificateOutlined } from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import { Popover, Space, Tag, Typography } from "antd";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { color, font, numericStyle, radius } from "theme/aiInventory";

import type { P0MCPContext } from "../types.ts";

export type P0MCPNodeData = {
  agentId: string;
  context: P0MCPContext;
};

/**
 * Double-border outlined treatment signals "control plane / seal of office":
 * an outer hairline rule plus an inner solid border, distinct from the
 * single-border filled integration node style.
 */
const nodeStyle: React.CSSProperties = {
  background: color.controlPlaneBg,
  border: `1px solid ${color.controlPlane}`,
  boxShadow: `inset 0 0 0 3px ${color.card}, inset 0 0 0 4px ${color.controlPlane}`,
  borderRadius: radius.card,
  padding: "10px 14px",
  color: color.controlPlaneInk,
  fontFamily: font.sans,
  fontSize: 12,
  fontWeight: 500,
  minWidth: 160,
  textAlign: "left",
  position: "relative",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const shieldStyle: React.CSSProperties = {
  color: color.controlPlane,
  fontSize: 14,
};

const kicker: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 9,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: color.controlPlane,
  marginBottom: 2,
};

const countStyle: React.CSSProperties = {
  ...numericStyle,
  color: color.controlPlaneInk,
  fontSize: 11,
};

const sectionHeading: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 9,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: color.inkMute,
  marginTop: 12,
  display: "block",
};

const PopoverContent: React.FC<{ ctx: P0MCPContext }> = ({ ctx }) => {
  const { orgSlug } = useParams();
  const policyHref = (ruleId: string): string =>
    `/o/${orgSlug}/${AppPaths.Policies}/edit/${ruleId}`;

  return (
    <Space direction="vertical" style={{ minWidth: 260 }} size={6}>
      <span style={{ ...sectionHeading, marginTop: 0 }}>
        Enforced agentic policies
      </span>
      {ctx.policies.length === 0 ? (
        <Typography.Text type="secondary">No agentic policies</Typography.Text>
      ) : (
        ctx.policies.map((p) => (
          <Link key={p.id} to={policyHref(p.id)}>
            <Tag color={p.enabled ? "purple" : "default"}>{p.name}</Tag>
          </Link>
        ))
      )}

      <span style={sectionHeading}>
        Managed servers · {ctx.managedServers.length}
      </span>
      {ctx.managedServers.length === 0 ? (
        <Typography.Text type="secondary">
          No servers registered
        </Typography.Text>
      ) : (
        ctx.managedServers.map((s) => (
          <div
            key={s.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
            }}
          >
            <span style={{ color: color.ink, fontWeight: 500 }}>{s.label}</span>
            <span
              style={{
                fontFamily: font.mono,
                fontSize: 10,
                color: color.inkMute,
              }}
            >
              {s.type} · {s.state}
            </span>
          </div>
        ))
      )}
    </Space>
  );
};

export const P0MCPNode: React.FC<NodeProps> = ({ data }) => {
  const typed = data as P0MCPNodeData;
  const popoverContent = useMemo(
    () => <PopoverContent ctx={typed.context} />,
    [typed.context]
  );

  return (
    <Popover content={popoverContent} trigger="click">
      <div style={nodeStyle}>
        <Handle type="target" position={Position.Left} />
        <SafetyCertificateOutlined style={shieldStyle} />
        <div>
          <div style={kicker}>P0 Gateway · control plane</div>
          <span style={countStyle}>
            {typed.context.enforcingCount} managed server
            {typed.context.enforcingCount === 1 ? "" : "s"}
          </span>
        </div>
        <Handle type="source" position={Position.Right} />
      </div>
    </Popover>
  );
};
